/*
================================================================================
FILE HEADER — apps/studio-web/src/ir/builder.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why               | What Changed           |
|----|------------|-------------------|-------------------|------------------------|
| 1  | 2026-06-23 | Claude Sonnet 4.6 | Phase 0 bootstrap | RawRecordedEvent → IR  |
| 2  | 2026-06-25 | Claude Sonnet 4.6 | Port locator algo | Use prebuiltSelector    |
|    |            |                   | from PLEX_Locator  | from extension when    |
|    |            |                   | _Algorithm.md      | available (has full    |
|    |            |                   |                   | tree uniqueness info)  |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/App.tsx

EXPORTS COVERED
---------------
- rawEventToStep(event, existingSteps)  — converts a raw recorded event to an IRStep
- buildTestIR(name, steps)              — wraps a step array into a valid TestIR document
- stepLabel(step)                       — human-readable label for display in the step list

CALLER → CALLEE MAP
--------------------
rawEventToStep()   → axNodeToCandidates() → builds Candidate[]
buildTestIR()      → TestIRSchema.parse()  → validates the final document

IMPORTANT POINTS
----------------
1. ARIA-first candidate ordering: a11yId > text > contentDesc > className > css.
   Positional CSS is never generated as a primary candidate.
2. The `description` field on the Selector is the semantic anchor used by
   Tier-1 ONNX healing. Make it as descriptive as possible.
3. For `navigate` events, we only add an openUrl step if the URL differs
   from the previous openUrl (avoids duplicate navigation steps on SPAs).
4. typeText events are accumulated — consecutive type events on the same
   element are merged into one step.
================================================================================
*/

import type { IRStep, Selector, Candidate, TestIR } from '@plex/ir';
import { newId } from '@plex/ir';
import type { RawRecordedEvent, SerializedAXNode } from '../extension/protocol.js';

// ---------------------------------------------------------------------------
// Convert AX node attributes to ordered ARIA candidates
// ---------------------------------------------------------------------------

function axNodeToCandidates(node: SerializedAXNode): Candidate[] {
  const candidates: Candidate[] = [];
  const attrs = node.attributes ?? {};

  // 1st priority: data-testid (maps to a11yId)
  const testId = attrs['data-testid'] ?? attrs['data-test-id'] ?? attrs['data-cy'] ?? attrs['data-qa'];
  if (testId) {
    candidates.push({ strategy: 'a11yId', value: testId, addedBy: 'recorder' });
  }

  // 2nd priority: visible text / accessible name (exact)
  if (node.name) {
    candidates.push({ strategy: 'text', value: node.name, addedBy: 'recorder' });
  }

  // 3rd priority: aria-label / content description
  const ariaLabel = attrs['aria-label'] ?? node.description;
  if (ariaLabel && ariaLabel !== node.name) {
    candidates.push({ strategy: 'contentDesc', value: ariaLabel, addedBy: 'recorder' });
  }

  // 4th priority: ARIA role (className strategy)
  if (node.role && node.role !== 'generic' && node.role !== 'none') {
    const roleName = node.name ?? node.description ?? '';
    if (roleName) {
      candidates.push({ strategy: 'className', value: `${node.role}[${roleName}]`, addedBy: 'recorder' });
    }
  }

  // Placeholder text for inputs
  if (node.placeholder) {
    candidates.push({ strategy: 'contentDesc', value: node.placeholder, addedBy: 'recorder' });
  }

  // Fallback: id attribute → css
  const idAttr = attrs['id'];
  if (idAttr) {
    candidates.push({ strategy: 'css', value: `#${idAttr}`, addedBy: 'recorder' });
  }

  return candidates.length > 0
    ? candidates
    : [{ strategy: 'css', value: node.role, addedBy: 'recorder' }];
}

function buildSelector(node: SerializedAXNode): Selector {
  const candidates = axNodeToCandidates(node);
  const descParts = [node.name, node.role, node.description].filter(Boolean);
  return {
    description: descParts.join(' ') || 'element',
    candidates,
  };
}

// ---------------------------------------------------------------------------
// Convert a raw event from the extension into an IR step
// ---------------------------------------------------------------------------

export function rawEventToStep(event: RawRecordedEvent): IRStep | null {
  const id = newId();

  if (event.eventType === 'navigate' && event.url) {
    return {
      type: 'openUrl',
      id,
      url: event.url,
      label: `Open ${new URL(event.url).hostname}`,
    };
  }

  if (event.eventType === 'click') {
    // Prefer prebuiltSelector (computed in background.ts with full AX tree context —
    // includes uniqueness checking and correct occurrence indices for non-unique elements).
    // Fall back to axNode-derived selector if prebuiltSelector is absent.
    let selector: Selector;

    if (event.prebuiltSelector && event.prebuiltSelector.candidates.length > 0) {
      selector = {
        description: event.prebuiltSelector.description,
        candidates: event.prebuiltSelector.candidates.map(c => ({
          strategy: c.strategy as Candidate['strategy'],
          value: c.value,
          ...(c.index !== undefined && c.index > 0 ? { index: c.index } : {}),
          addedBy: 'recorder' as const,
        })),
      };
    } else if (event.axNode) {
      selector = buildSelector(event.axNode);
    } else {
      // No element info — coordinate fallback
      return {
        type: 'tap',
        id,
        selector: {
          description: `Element at (${event.x ?? 0}, ${event.y ?? 0})`,
          candidates: [{ strategy: 'css', value: '*', addedBy: 'recorder' }],
        },
        label: `Tap at (${event.x ?? 0}, ${event.y ?? 0})`,
      };
    }

    return {
      type: 'tap',
      id,
      selector,
      label: `Tap ${selector.description}`,
    };
  }

  if (event.eventType === 'type' && event.text !== undefined) {
    return {
      type: 'typeText',
      id,
      text: event.text,
      label: `Type "${event.text.length > 20 ? event.text.slice(0, 20) + '…' : event.text}"`,
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Wrap steps into a valid TestIR document
// ---------------------------------------------------------------------------

export function buildTestIR(name: string, steps: IRStep[]): TestIR {
  return {
    irVersion: '1.0',
    meta: {
      id: newId(),
      name,
      tags: [],
      createdBy: 'recorder',
    },
    config: {
      targets: ['web'],
      env: {},
      dataSources: [],
      interruptionPolicy: 'auto',
      healing: { maxTierUsed: 1, minConfidence: 0.7 },
      timeouts: { step: 15000, suite: 300000 },
    },
    flows: [
      {
        id: newId(),
        name: 'main',
        entry: true,
        steps,
      },
    ],
    hooks: { beforeEach: [], afterEach: [] },
  };
}

// ---------------------------------------------------------------------------
// Human-readable label for a step (for display in the step list)
// ---------------------------------------------------------------------------

export function stepLabel(step: IRStep): string {
  if (step.label) return step.label;

  switch (step.type) {
    case 'openUrl':     return `Open ${step.url}`;
    case 'tap':         return `Tap ${step.selector.description}`;
    case 'typeText':    return `Type "${step.text.length > 30 ? step.text.slice(0, 30) + '…' : step.text}"`;
    case 'assert':      return `Assert ${step.selector.description} ${step.assertion}`;
    case 'waitFor':     return `Wait for ${step.selector.description}`;
    case 'screenshot':  return 'Screenshot';
    case 'wait':        return `Wait ${step.durationMs}ms`;
    default:            return step.type;
  }
}
